import { useEffect, useRef, useState } from 'react';

import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import { createDisplayIdentityApi, type DisplayIdentity, type DisplayIdentityApi } from './displayIdentityApi';

const EMPTY_DISPLAY_IDENTITY: DisplayIdentity = { personName: null, brokerageName: null };

export type UseDisplayIdentityOptions = {
  agentId: string | null | undefined;
  api?: DisplayIdentityApi;
};

export type UseDisplayIdentityResult = DisplayIdentity & {
  loading: boolean;
};

const isUsableAgentId = (agentId: string | null | undefined): agentId is string =>
  typeof agentId === 'string' && agentId.trim().length > 0;

export const useDisplayIdentity = ({ agentId, api }: UseDisplayIdentityOptions): UseDisplayIdentityResult => {
  const defaultApiRef = useRef<DisplayIdentityApi | null>(null);
  const identityApi = api ?? (defaultApiRef.current ??= createDisplayIdentityApi({ env: resolveClientEnv() }));
  const [state, setState] = useState<UseDisplayIdentityResult>(() => ({
    ...EMPTY_DISPLAY_IDENTITY,
    loading: isUsableAgentId(agentId),
  }));

  useEffect(() => {
    if (!isUsableAgentId(agentId)) {
      setState({ ...EMPTY_DISPLAY_IDENTITY, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setState({ ...EMPTY_DISPLAY_IDENTITY, loading: true });

    void identityApi
      .getIdentity(agentId, { signal: controller.signal })
      .then(identity => {
        if (!active || controller.signal.aborted) return;

        setState({ ...identity, loading: false });
      })
      .catch(() => {
        if (!active || controller.signal.aborted) return;

        setState({ ...EMPTY_DISPLAY_IDENTITY, loading: false });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [agentId, identityApi]);

  return state;
};
